import {hasFeatureFlag} from '../../forecast-app/shared/util/FeatureUtil';
import {trackEvent} from './TrackingV2';

/** Which editor raised the event. The three surfaces fail in different ways and are flagged separately. */
export const EDITOR_SURFACE = {
	description: 'Description',
	newComment: 'New Comment',
	commentEdit: 'Comment Edit',
} as const;

export type EditorSurface = (typeof EDITOR_SURFACE)[keyof typeof EDITOR_SURFACE];

const OBJECT = 'Rich Text Editor';
const MEDIA_OBJECT = 'Inline Media';

/**
 * A fixed allowance of events, so a per-editor signal cannot scale with the page.
 *
 * A task modal mounts one editor per comment, so an unbudgeted per-editor event is
 * one event per comment per open — a busy task would account for hundreds on its
 * own, and the noisiest tasks would drown out everything else precisely because they
 * are the ones most likely to be interesting.
 */
export const createEventBudget = (limit: number): {spend: () => boolean} => {
	let spent = 0;
	return {
		spend: () => {
			if (spent >= limit) return false;
			spent += 1;
			return true;
		},
	};
};

/**
 * Drift is reported at most this many times per session, across every editor and task.
 *
 * A handful of examples is enough to identify a failing construct; the rate at which
 * it happens is not something this event is trying to measure.
 */
const driftBudget = createEventBudget(5);

/** The rollout state the event happened under, so the cohort can be split in analytics. */
const editorFlags = () => ({
	plateJs: hasFeatureFlag('plate_js_task_modal'),
	plateJsComments: hasFeatureFlag('plate_js_task_modal_comments'),
});

export const trackEditorOpened = (surface: EditorSurface): void => {
	trackEvent(OBJECT, 'Opened', {surface, ...editorFlags()});
};

export const trackEditorSaveSucceeded = (surface: EditorSurface): void => {
	trackEvent(OBJECT, 'Save Succeeded', {surface, ...editorFlags()});
};

/**
 * A save that did not happen, and why.
 *
 * Save-success rate per company is the metric this exists for: a drop in saves with
 * no matching rise in errors is the shape formatting degradation actually takes, and
 * nothing else on the page would show it.
 */
export const trackEditorSaveFailed = (surface: EditorSurface, reason: string): void => {
	trackEvent(OBJECT, 'Save Failed', {surface, reason, ...editorFlags()});
};

/**
 * The outcome of an inline upload.
 *
 * The filename is deliberately not sent — it is customer data, and the kind, type and
 * size are what characterise a failure.
 */
export const trackInlineMediaUpload = (
	outcome: 'succeeded' | 'failed',
	media: {kind?: string; mimeType?: string; sizeBytes?: number; fileName?: string}
): void => {
	trackEvent(MEDIA_OBJECT, outcome === 'succeeded' ? 'Upload Succeeded' : 'Upload Failed', {
		kind: media.kind,
		mimeType: media.mimeType,
		sizeBytes: media.sizeBytes,
		...editorFlags(),
	});
};

/**
 * A stored reference that could not be turned into something loadable.
 *
 * This is a broken image sitting in a customer's description. It produces no error,
 * no failed request we can see, and no complaint until someone notices the gap.
 */
export const trackInlineMediaResolveFailed = (kind?: string): void => {
	trackEvent(MEDIA_OBJECT, 'Resolve Failed', {kind, ...editorFlags()});
};

/**
 * Inline references that left the content in one edit.
 *
 * The intended case is a user deleting an image, which is ordinary. The case worth
 * watching is the same event arriving without the user having done anything — media
 * disappearing from a description on its own is the failure this rollout exists to
 * catch, and it looks identical from the outside until you can count it.
 *
 * Silent at zero: every edit that touches no media would otherwise raise an event.
 */
export const trackInlineMediaRemoved = (count: number): void => {
	if (count <= 0) return;

	trackEvent(MEDIA_OBJECT, 'Removed', {count, ...editorFlags()});
};

/**
 * Content that does not survive an open-and-blur cycle, and which constructs are involved.
 *
 * Budgeted rather than sent every time: the block types repeat across tasks, so a few
 * examples identify the construct and the rest is volume.
 *
 * Arrays are flattened to comma-joined strings because analytics properties are
 * filtered as scalars — an array lands as an opaque object that cannot be grouped by.
 */
export const trackRoundTripDrift = (
	surface: EditorSurface,
	drift: {
		blockTypes: string[];
		blockCountBefore: number;
		blockCountAfter: number;
		entityTypesBefore: string[];
		entityTypesAfter: string[];
	},
	budget: {spend: () => boolean} = driftBudget
): void => {
	if (!budget.spend()) return;

	const entityTypesLost = drift.entityTypesBefore.filter(type => !drift.entityTypesAfter.includes(type));

	trackEvent(OBJECT, 'Round Trip Drift', {
		surface,
		blockTypes: drift.blockTypes.join(','),
		blockCountBefore: drift.blockCountBefore,
		blockCountAfter: drift.blockCountAfter,
		entityTypesLost: entityTypesLost.join(','),
		...editorFlags(),
	});
};
